import { SearchFocusTarget, User, NavigateOptions } from "./types";
import { Home } from "@/components/Home";
import { Login } from "@/components/Login";
import { Register } from "@/components/Register";
import { SearchPage } from "@/components/SearchPage";
import { AccountOverview } from "@/components/AccountOverview";
import { Statistics } from "@/components/Statistics";
import { AdminDashboard } from "@/components/AdminDashboard";
import { ErrorPage, ErrorVariant } from "@/components/ErrorPage";
import { LegalNotice } from "@/components/LegalNotice";
import { Privacy } from "@/components/Privacy";
import { TermsOfService } from "@/components/TermsOfService";
import { Help } from "@/components/Help";
import { WebsiteSuggest } from "@/components/WebsiteSuggest";
import type { WebsiteSuggestion } from "@/components/website-suggest/types";
import { WebsiteManagement, Website } from "@/components/WebsiteManagement";
import type { BroadcastMessage } from "@/components/BroadcastBanner";
import VerifyEmail from "@/components/account/VerifyEmail"
import VerifyAccount from "@/components/VerifyAccount"
import ResetPassword from "@/components/ResetPassword";
import EmailChangeInfo from "@/components/EmailChangeInfo";

type LoginLockInfo = {
  email: string;
  lockedUntil?: string | null;
  messageKey: string;
};

interface AppRoutesProps {
  currentPage: string;
  navigate: (page: string, options?: NavigateOptions) => void;
  currentUser: User | null;
  maintenanceEnabled: boolean;
  onLogout: () => void;
  onSearch: (query: string) => void;
  searchQuery: string;
  focusTarget: SearchFocusTarget | null;
  onFocusHandled: () => void;
  onNavigateToSearchPage: (target: SearchFocusTarget) => void;
  onLogin: (email: string, password: string) => Promise<void>;
  loginLockInfo: LoginLockInfo | null;
  onClearLoginLock: () => void;
  loginErrorKey: string | null;
  onClearLoginError: () => void;
  suggestions: WebsiteSuggestion[];
  onSuggestWebsite: (suggestion: WebsiteSuggestion) => void;
  onApproveSuggestion: (
    id: string,
    modified?: Partial<WebsiteSuggestion>
  ) => void;
  onRejectSuggestion: (id: string) => void;
  onSaveSuggestion: (
    id: string,
    modified: Partial<WebsiteSuggestion>
  ) => Promise<void>;
  onAddWebsite: (
    website: Omit<Website, "id" | "addedAt" | "addedBy">
  ) => Promise<void>;
  onEditWebsite: (id: string, updates: Partial<Website>) => void;
  onDeleteWebsite: (id: string) => void;
  onScheduleBroadcast: (broadcast: BroadcastMessage) => void;
}

const getErrorVariantFromPage = (page: string): ErrorVariant => {
  switch (page) {
    case "error-403":
      return "403";
    case "error-500":
      return "500";
    case "error-maintenance":
      return "maintenance";
    case "error":
    case "error-404":
    default:
      return "404";
  }
};

const renderErrorPage = (
  variant: ErrorVariant,
  props: Pick<
    AppRoutesProps,
    "navigate" | "currentUser" | "onLogout" | "onNavigateToSearchPage"
  >
) => (
  <ErrorPage
    navigate={props.navigate}
    currentUser={props.currentUser}
    onLogout={props.onLogout}
    onNavigateToSearchPage={props.onNavigateToSearchPage}
    variant={variant}
  />
);

export function AppRoutes({
  currentPage,
  navigate,
  currentUser,
  maintenanceEnabled,
  onLogout,
  onSearch,
  searchQuery,
  focusTarget,
  onFocusHandled,
  onNavigateToSearchPage,
  onLogin,
  loginLockInfo,
  onClearLoginLock,
  loginErrorKey,
  onClearLoginError,
  suggestions,
  onSuggestWebsite,
  onApproveSuggestion,
  onRejectSuggestion,
  onSaveSuggestion,
  onAddWebsite,
  onEditWebsite,
  onDeleteWebsite,
  onScheduleBroadcast,
}: AppRoutesProps) {
  const isAdmin = currentUser?.role === "admin";
  if (maintenanceEnabled && !isAdmin) {
    return (
      <Login
        navigate={navigate}
        onLogin={onLogin}
        loginLockInfo={loginLockInfo}
        onClearLoginLock={onClearLoginLock}
        loginErrorKey={loginErrorKey}
        onClearLoginError={onClearLoginError}
        maintenanceEnabled={maintenanceEnabled}
        onNavigateToSearchPage={onNavigateToSearchPage}
      />
    );
  }

  switch (currentPage) {
    case "home":
      return (
        <Home
          navigate={navigate}
          currentUser={currentUser}
          onLogout={onLogout}
          onSearch={onSearch}
          onNavigateToSearchPage={onNavigateToSearchPage}
        />
      );
    case "login":
      return (
        <Login
          navigate={navigate}
          onLogin={onLogin}
          loginLockInfo={loginLockInfo}
          onClearLoginLock={onClearLoginLock}
          loginErrorKey={loginErrorKey}
          onClearLoginError={onClearLoginError}
          maintenanceEnabled={maintenanceEnabled}
          onNavigateToSearchPage={onNavigateToSearchPage}
        />
      );
    case "register":
      return (
        <Register
          navigate={navigate}
          onNavigateToSearchPage={onNavigateToSearchPage}
        />
      );

    case "verify-email":
      return (
      <VerifyEmail
        navigate={navigate}
        />
      );
    case "verify-account":
      return (
      <VerifyAccount
        navigate={navigate}
        />
      );
    case "reset-password":
      return (
      <ResetPassword
        navigate={navigate}
        />
      );
    case "email-change-info":
      return <EmailChangeInfo navigate={navigate} />;
    case "search":
      return (
        <SearchPage
          navigate={navigate}
          currentUser={currentUser}
          onLogout={onLogout}
          onSearch={onSearch}
          initialQuery={searchQuery}
          focusTarget={focusTarget}
          onFocusHandled={onFocusHandled}
          onNavigateToSearchPage={onNavigateToSearchPage}
          pageKey="search"
        />
      );
    case "categories":
      return (
        <SearchPage
          navigate={navigate}
          currentUser={currentUser}
          onLogout={onLogout}
          onSearch={onSearch}
          focusTarget={focusTarget}
          onFocusHandled={onFocusHandled}
          onNavigateToSearchPage={onNavigateToSearchPage}
          pageKey="categories"
        />
      );
    case "account":
      return (
        <AccountOverview
          navigate={navigate}
          currentUser={currentUser}
          onLogout={onLogout}
          onSearch={onSearch}
          onNavigateToSearchPage={onNavigateToSearchPage}
        />
      );
    case "statistics":
      return (
        <Statistics
          navigate={navigate}
          currentUser={currentUser}
          onLogout={onLogout}
          onSearch={onSearch}
          onNavigateToSearchPage={onNavigateToSearchPage}
        />
      );
    case "admin":
      return (
        <AdminDashboard
          navigate={navigate}
          currentUser={currentUser}
          onLogout={onLogout}
          onSearch={onSearch}
          onScheduleBroadcast={onScheduleBroadcast}
          onNavigateToSearchPage={onNavigateToSearchPage}
        />
      );
    case "suggest-website":
      return (
        <WebsiteSuggest
          navigate={navigate}
          currentUser={currentUser}
          onLogout={onLogout}
          onSuggest={onSuggestWebsite}
          onNavigateToSearchPage={onNavigateToSearchPage}
        />
      );
    case "manage-websites":
      return (
        <WebsiteManagement
          navigate={navigate}
          currentUser={currentUser}
          onLogout={onLogout}
          suggestions={suggestions}
          onApproveSuggestion={onApproveSuggestion}
          onRejectSuggestion={onRejectSuggestion}
          onSaveSuggestion={onSaveSuggestion}
          onAddWebsite={onAddWebsite}
          onEditWebsite={onEditWebsite}
          onDeleteWebsite={onDeleteWebsite}
          onNavigateToSearchPage={onNavigateToSearchPage}
        />
      );
    case "impressum":
      return (
        <LegalNotice
          navigate={navigate}
          currentUser={currentUser}
          onLogout={onLogout}
          onNavigateToSearchPage={onNavigateToSearchPage}
        />
      );
    case "datenschutz":
      return (
        <Privacy
          navigate={navigate}
          currentUser={currentUser}
          onLogout={onLogout}
          onNavigateToSearchPage={onNavigateToSearchPage}
        />
      );
    case "nutzungsbedingungen":
      return (
        <TermsOfService
          navigate={navigate}
          currentUser={currentUser}
          onLogout={onLogout}
          onNavigateToSearchPage={onNavigateToSearchPage}
        />
      );
    case "hilfe":
      return (
        <Help
          navigate={navigate}
          currentUser={currentUser}
          onLogout={onLogout}
          onNavigateToSearchPage={onNavigateToSearchPage}
        />
      );
    case "error":
    case "error-404":
    case "error-403":
    case "error-500":
    case "error-maintenance":
      return renderErrorPage(getErrorVariantFromPage(currentPage), {
        navigate,
        currentUser,
        onLogout,
        onNavigateToSearchPage,
      });
    default:
      return renderErrorPage(getErrorVariantFromPage(currentPage), {
        navigate,
        currentUser,
        onLogout,
        onNavigateToSearchPage,
      });
  }
}
